import React from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import {
  BadgeCheck,
  Check,
  CircleHelp,
  LogIn,
  Send,
  Star,
  UserPlus,
} from "lucide-react";
import {
  CtaSection,
  PageHero,
  Section,
  SectionHeading,
  btnOutlineDark,
  btnPrimary,
  cardClass,
  cardIconClass,
  useAuthActions,
} from "../components/SiteUI";

const defaultPlans = [
  {
    name: "Starter",
    monthly: 1500,
    yearly: 16200,
    description: "Best for small landlords starting digital operations",
    features: ["Up to 10 properties", "Tenant and lease records", "Payments and invoices", "Email support"],
    featured: false,
  },
  {
    name: "Professional",
    monthly: 4500,
    yearly: 48600,
    description: "For growing teams with multi-site portfolios",
    features: ["Up to 50 properties", "Advanced reports", "Maintenance tracking", "Priority support"],
    featured: true,
  },
  {
    name: "Enterprise",
    monthly: 12000,
    yearly: 129600,
    description: "For complex operations and organizations",
    features: ["Unlimited properties", "Unlimited users", "Portfolio analytics", "Dedicated support"],
    featured: false,
  },
];

const Pricing = () => {
  const navigate = useNavigate();
  const { openSignup } = useAuthActions();
  const [billing, setBilling] = React.useState("monthly");
  const [plans, setPlans] = React.useState(defaultPlans);

  React.useEffect(() => {
    const loadPlans = async () => {
      try {
        const res = await api.get("/subscription-plans/public");
        const data = res.data?.data;
        if (Array.isArray(data) && data.length > 0) {
          setPlans(data.map((plan) => ({
            name: plan.name,
            monthly: Number(plan.monthlyPrice || 0),
            yearly: Number(plan.yearlyPrice || 0),
            description: plan.description || "",
            features: Array.isArray(plan.features) ? plan.features : [],
            featured: Boolean(plan.featured)
          })));
        }
      } catch (error) {
        console.error("Failed to load public pricing plans:", error);
      }
    };

    loadPlans();
  }, []);

  const faqs = [
    ["How do I activate after signup?", "After signup, contact Gamrent admin with your payment evidence. Your dashboard is enabled after approval."],
    ["Can I switch between Monthly and Yearly?", "Yes. SuperAdmin can update your billing cycle and subscription period during approval or renewal."],
    ["What currency is used?", "All prices are in GMD (Dalasi)."],
  ];

  return (
    <div className="bg-teal-50">
      <PageHero
        eyebrow="Pricing"
        title="Simple Plans for Every Stage"
        description="Choose the package that matches your portfolio size and workflow needs. Billing is configured in GMD (Dalasi)."
      >
        <div className="mt-8 inline-flex p-1 rounded-md bg-white/10 border border-white/20">
          <button
            onClick={() => setBilling("monthly")}
            className={`px-5 py-2 text-sm font-bold rounded-md transition-colors ${billing === "monthly" ? "bg-teal-50 text-teal-900" : "text-white"}`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBilling("yearly")}
            className={`px-5 py-2 text-sm font-bold rounded-md transition-colors ${billing === "yearly" ? "bg-teal-50 text-teal-900" : "text-white"}`}
          >
            Yearly
          </button>
        </div>
        <p className="mt-4 text-xs font-semibold uppercase tracking-[0.12em] text-teal-200">All listed prices are in GMD</p>
      </PageHero>

      <Section>
        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {plans.length === 0 ? (
            <p className="md:col-span-3 text-center text-teal-800">Pricing plans will appear here once they are published.</p>
          ) : (
            plans.map((plan, idx) => (
            <article
              key={plan.name}
              className={`reveal visible reveal-delay-${idx + 1} ${cardClass} flex flex-col ${plan.featured ? "border-teal-500 ring-1 ring-teal-500 shadow-xl" : "lift"}`}
            >
              {plan.featured && (
                <span className="inline-flex items-center gap-1.5 self-start mb-3 px-3 py-1 rounded-full bg-teal-100 text-teal-700 text-xs font-bold uppercase tracking-wider">
                  <Star className="w-3.5 h-3.5" aria-hidden="true" />
                  Popular
                </span>
              )}
              <h2 className="text-xl font-extrabold text-teal-900">{plan.name}</h2>
              <p className="text-sm text-teal-700 mt-2">{plan.description}</p>
              <p className="mt-6 text-3xl sm:text-4xl font-extrabold text-teal-700 break-words">
                GMD {billing === "monthly" ? plan.monthly.toLocaleString() : plan.yearly.toLocaleString()}
                <span className="text-sm text-teal-700 font-semibold">/{billing === "monthly" ? "mo" : "yr"}</span>
              </p>

              <ul className="mt-6 space-y-3 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature} className="text-sm text-teal-800 flex items-start gap-3">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-teal-700" aria-hidden="true" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button onClick={openSignup} className={`mt-7 ${btnPrimary}`}>
                Choose {plan.name}
              </button>
            </article>
          ))
          )}
        </div>
      </Section>

      <Section tone="alt" width="max-w-5xl">
        <SectionHeading title="Activation Process" />
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { text: "Create your company account", icon: UserPlus },
            { text: "Sign in with your registered email", icon: LogIn },
            { text: "Share payment proof with Gamrent admin", icon: Send },
            { text: "Get access after approval", icon: BadgeCheck }
          ].map(({ text, icon: Icon }, idx) => (
            <div key={text} className={`reveal reveal-delay-${idx + 1} lift ${cardClass}`}>
              <div className={`${cardIconClass} mb-3`} aria-hidden="true">
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-700">Step {idx + 1}</p>
              <p className="text-sm text-teal-800 mt-2">{text}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section width="max-w-4xl">
        <SectionHeading title="Frequently Asked Questions" />
        <div className="space-y-4">
          {faqs.map(([q, a], idx) => (
            <article key={q} className={`reveal reveal-delay-${idx + 1} ${cardClass}`}>
              <h3 className="text-base font-bold text-teal-900 flex items-start gap-2.5">
                <CircleHelp className="w-5 h-5 shrink-0 text-teal-700" aria-hidden="true" />
                {q}
              </h3>
              <p className="text-sm text-teal-800 mt-2">{a}</p>
            </article>
          ))}
        </div>
      </Section>

      <CtaSection title="Need a custom setup for your organization?">
        <button onClick={() => navigate("/contact")} className={btnPrimary}>
          Contact Sales
        </button>
        <button onClick={openSignup} className={btnOutlineDark}>
          Create Account
        </button>
      </CtaSection>
    </div>
  );
};

export default Pricing;
